import { BalanceData, Rarity } from "../data/balance";
import { Pcg32 } from "../maths/pcg32";
import { chestOfRarity, chestShare, metaSeed, SECONDS_PER_DAY, utcDay } from "./orders";
import { PlayerProfile } from "./profile";
import { Wallet } from "./wallet";

/**
 * The offline Shop's rules (ADR 0013, DESIGN §8.1, §9.5, §10.4; balance/src/shop.json): everything here costs gems.
 * Speed-up packs, resource packs scaled to the player's level and capped per day, and Supply Crates
 * (D6) whose contents are rolled from (player, UTC day, slot), shown before paying and bought exactly as shown. Gems
 * never buy levels, and never gear here (the Black Market, meta/market.ts, is the one place they do).
 */

/** Pcg32 stream for a crate's rarity roll, apart from every other use of the same seed. */
const CRATE_STREAM = 307;
const SECONDS_PER_MINUTE = 60;
/** Seed tag and per-day stride of a VIP extra crate (apart from the data's slots' seeds). */
const VIP_CRATE_TAG = "crateVip";
const VIP_CRATE_STRIDE = 16;

/**
 * The Shop's product ids that cost gems (the real-money gem packs are the Store port's, app/purchases.ts). BUILDER_PRODUCT
 * is refused since builders became real money (2026-10-19): kept so older callers compile.
 */
export const BUILDER_PRODUCT = "builder";
export const CRATE_PRODUCT_PREFIX = "crate.";
/** The free daily crate's slot (MP-08): first in the row, apart from the data's paid slots. */
export const FREE_CRATE_SLOT = -1;
const FREE_CRATE_TAG = "crateFree";

// ------------------------------------------------------------------ builders (docs/SHOP.md §18)

/**
 * @deprecated Builders are real money only since 2026-10-19 (meta/builders.ts, offers.json page `builders`): no
 * builder has a gem price, so this is always null. Kept so older callers compile; use `nextBuilderOffer`.
 */
export function nextBuilderPrice(_balance: BalanceData, _buildersOwned: number): number | null { return null; }

/**
 * @deprecated Builders are sold only through the Store (a builder offer granted by app deals.ts, meta/builders.ts
 * hireBuilder); gems never buy one. Always refuses, spending nothing.
 */
export function buyBuilder(_profile: PlayerProfile, _wallet: Wallet, _now: number): true | string { return BUILDERS_REAL_MONEY; }

/** Why the gem path refuses a builder (the Store sells builders). */
export const BUILDERS_REAL_MONEY = "builders are sold in the Store";

// ------------------------------------------------------------------ packs

export function buySpeedUpPack(profile: PlayerProfile, wallet: Wallet, id: string, now: number): true | string {
  const pack = profile.balance.commerce.shop.speedUpPacks.find((p) => p.id === id);
  if (!pack) return "no such product";
  const paid = wallet.spend("gems", pack.gems, "speedUpPack", now, id);
  if (paid !== true) return paid;
  profile.addSpeedUp(pack.minutes);
  return true;
}

/** A resource pack's gold at the player's highest cleared level. */
export function resourcePackContents(balance: BalanceData, id: string, level: number): number | null {
  const pack = balance.commerce.shop.resourcePacks.find((p) => p.id === id);
  return pack ? chestShare(balance, Math.max(1, level), pack.chests) : null;
}

// ------------------------------------------------------------------ Supply Crates (D6)

/** A crate as shown before paying: its rarity, price and exact contents. */
export interface CrateOffer {
  readonly slot: number;
  readonly day: number;
  /** The highest cleared level it was rolled at (the contents scale with it). */
  readonly level: number;
  readonly rarity: Rarity;
  readonly gems: number;
  readonly contents: { readonly gold: number; readonly speedUpMinutes: number };
}

/**
 * The crate in `slot` on UTC `day` for this player at `level`: deterministic, so the preview is the purchase. `extraSlots`:
 * the VIP perk's crates a day beyond the data's (docs/SHOP.md §2), rolled from their own seed tag.
 */
export function crateOffer(balance: BalanceData, playerId: string, day: number, slot: number, level: number, extraSlots = 0): CrateOffer | null {
  const c = balance.commerce.shop.crates;
  if (slot === FREE_CRATE_SLOT) return freeCrateOffer(balance, playerId, day, level);
  if (c.slotsPerDay <= 0 || slot < 0 || slot >= c.slotsPerDay + Math.max(0, extraSlots)) return null;
  const seed = slot < c.slotsPerDay ? metaSeed(playerId, "crate", day * c.slotsPerDay + slot) : metaSeed(playerId, VIP_CRATE_TAG, day * VIP_CRATE_STRIDE + slot - c.slotsPerDay);
  const total = c.weights.reduce((a, w) => a + w, 0);
  if (total <= 0) return null;
  let r = new Pcg32(seed, CRATE_STREAM).nextInt(total), rarity = Rarity.Common;
  for (let i = 0; i < c.weights.length; i++) { r -= c.weights[i]; if (r < 0) { rarity = i as Rarity; break; } }
  const L = Math.max(1, level);
  // The chest rules give a crate its size; its gear is left out (gems never buy gear outside the Black Market).
  const chest = chestOfRarity(balance, L, rarity, seed);
  return { slot, day, level: L, rarity, gems: c.gems[rarity], contents: { gold: chest.gold, speedUpMinutes: chest.speedUpMinutes } };
}

/** The day's free crate (MP-08): `freeRarity`, rolled from its own seed, gems 0; null when the data has none. */
function freeCrateOffer(balance: BalanceData, playerId: string, day: number, level: number): CrateOffer | null {
  const c = balance.commerce.shop.crates;
  if (c.freePerDay <= 0) return null;
  const L = Math.max(1, level), chest = chestOfRarity(balance, L, c.freeRarity, metaSeed(playerId, FREE_CRATE_TAG, day));
  return { slot: FREE_CRATE_SLOT, day, level: L, rarity: c.freeRarity, gems: 0, contents: { gold: chest.gold, speedUpMinutes: chest.speedUpMinutes } };
}

export function sameCrate(a: CrateOffer, b: CrateOffer): boolean {
  return a.slot === b.slot && a.day === b.day && a.level === b.level && a.rarity === b.rarity && a.gems === b.gems
    && a.contents.gold === b.contents.gold && a.contents.speedUpMinutes === b.contents.speedUpMinutes;
}

// ------------------------------------------------------------------ the day's purchases (the `shop` save section)

/** `free`: today's free crate is claimed (MP-08; absent before it). */
export interface ShopJson { day: number; crates: number[]; packs: Record<string, number>; lastLossAt: number; free?: boolean }

/** What was bought today (the daily caps) and when the last loss was (the never list's quiet window). */
export class ShopState {
  /** The UTC day `crates` and `packs` count for. */
  day = -1;
  /** Crate slots bought today. */
  crates: number[] = [];
  /** Today's free crate is claimed (MP-08). */
  freeCrate = false;
  /** Resource packs bought today, by id. */
  packs: Record<string, number> = {};
  /** When the player last lost a level (Unix seconds; −1 never). */
  lastLossAt = -1;
  /**
   * VIP's `extraCrateSlots` perk, set by @highmarch/app Deals (derived, not saved). Supply Crates are gone (owner
   * 2026-10-21): the perk now adds that many buys a day to every gold pack's cap.
   */
  extraCrateSlots = 0;

  /** Starts a new day's counts when the UTC day changed (a clock set backwards keeps today's). */
  private roll(now: number): void {
    const today = utcDay(now);
    if (today > this.day) { this.day = today; this.crates = []; this.packs = {}; this.freeCrate = false; }
  }

  crateBought(now: number, slot: number): boolean { this.roll(now); return slot === FREE_CRATE_SLOT ? this.freeCrate : this.crates.includes(slot); }
  packsBought(now: number, id: string): number { this.roll(now); return this.packs[id] ?? 0; }

  noteResult(won: boolean, now: number): void { if (!won) this.lastLossAt = now; }

  /** Today's free crate (MP-08) for this player at `level`, with whether it is claimed; null when the data has none. */
  freeCrateOffer(balance: BalanceData, playerId: string, now: number, level: number): { offer: CrateOffer; bought: boolean } | null {
    const offer = freeCrateOffer(balance, playerId, utcDay(now), level);
    return offer ? { offer, bought: this.crateBought(now, FREE_CRATE_SLOT) } : null;
  }

  /** Today's paid crates for this player at `level`, with whether each is already bought. */
  crateOffers(balance: BalanceData, playerId: string, now: number, level: number): { offer: CrateOffer; bought: boolean }[] {
    const out: { offer: CrateOffer; bought: boolean }[] = [];
    if (balance.commerce.shop.crates.slotsPerDay <= 0) return out;   // retired (docs/SHOP.md §19): VIP adds none either
    for (let s = 0; s < balance.commerce.shop.crates.slotsPerDay + this.extraCrateSlots; s++) {
      const offer = crateOffer(balance, playerId, utcDay(now), s, level, this.extraCrateSlots);
      if (offer) out.push({ offer, bought: this.crateBought(now, s) });
    }
    return out;
  }

  /**
   * Buys the crate the player was shown (the free one, MP-08, for nothing): refused when today's roll no longer matches it (the day turned, or a new
   * level cleared changed its size) so nobody pays for something other than what they saw, and once a slot a day.
   */
  buyCrate(balance: BalanceData, playerId: string, profile: PlayerProfile, wallet: Wallet, shown: CrateOffer, now: number, level: number): true | string {
    const today = utcDay(now);
    const offer = crateOffer(balance, playerId, today, shown.slot, level, this.extraCrateSlots);
    if (offer === null) return "no such crate";
    if (!sameCrate(offer, shown)) return "offer changed";
    if (this.crateBought(now, shown.slot)) return "already bought today";
    if (offer.slot === FREE_CRATE_SLOT) this.freeCrate = true;
    else {
      const paid = wallet.spend("gems", offer.gems, "crate", now, `${CRATE_PRODUCT_PREFIX}${offer.slot}@${today}`);
      if (paid !== true) return paid;
      this.crates.push(offer.slot);
    }
    profile.addGold(offer.contents.gold);
    profile.addSpeedUp(offer.contents.speedUpMinutes);
    return true;
  }

  /** A gold pack's daily cap, with VIP's extra buys (see `extraCrateSlots`). */
  packCap(perDay: number): number { return perDay + this.extraCrateSlots; }

  /** Whether today's free gold pack (a pack with `freePerDay`) is still to claim. */
  freePackOpen(now: number): boolean { this.roll(now); return !this.freeCrate; }

  /**
   * Claims today's free gold pack (owner 2026-10-21: it replaced the free Supply Crate; the save's `free` flag carries
   * over): the first pack with `freePerDay`, once a UTC day, for nothing, apart from its paid cap.
   */
  claimFreePack(profile: PlayerProfile, now: number, level: number): number | string {
    const pack = profile.balance.commerce.shop.resourcePacks.find((p) => p.freePerDay > 0);
    const contents = pack ? resourcePackContents(profile.balance, pack.id, level) : null;
    if (!pack || contents === null) return "no such product";
    if (!this.freePackOpen(now)) return "already bought today";
    this.freeCrate = true;
    profile.addGold(contents);
    return contents;
  }

  /** Buys a resource pack at the player's level (`level`: the highest cleared), once it is open, within its daily cap. */
  buyResourcePack(profile: PlayerProfile, wallet: Wallet, id: string, now: number, level: number): number | string {
    const pack = profile.balance.commerce.shop.resourcePacks.find((p) => p.id === id);
    const contents = resourcePackContents(profile.balance, id, level);
    if (!pack || contents === null) return "no such product";
    if (level < pack.unlockAfterLevel) return "locked";
    if (this.packsBought(now, id) >= this.packCap(pack.perDay)) return "daily limit reached";
    const paid = wallet.spend("gems", pack.gems, "resourcePack", now, id);
    if (paid !== true) return paid;
    this.packs[id] = (this.packs[id] ?? 0) + 1;
    profile.addGold(contents);
    return contents;
  }

  toJson(): ShopJson { return { day: this.day, crates: [...this.crates], packs: { ...this.packs }, lastLossAt: this.lastLossAt, free: this.freeCrate }; }

  static fromJson(j: unknown): ShopState {
    const s = new ShopState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<ShopJson>;
    if (Number.isInteger(src.day)) s.day = src.day!;
    if (Array.isArray(src.crates)) s.crates = src.crates.filter((n) => Number.isInteger(n) && n >= 0);
    if (typeof src.packs === "object" && src.packs !== null && !Array.isArray(src.packs)) {
      for (const [k, n] of Object.entries(src.packs)) if (Number.isInteger(n) && n > 0) s.packs[k] = n;
    }
    if (Number.isInteger(src.lastLossAt)) s.lastLossAt = src.lastLossAt!;
    s.freeCrate = src.free === true;
    return s;
  }
}

// ------------------------------------------------------------------ the never list (ENGAGEMENT C, E10)

/** Where the game is when something might put an offer in front of the player. */
export type OfferScreen = "hub" | "level" | "levelEnd";

/**
 * Whether the game may put an offer in front of the player (a nudge, a featured deal, a purchase prompt): never in a
 * level, never on the end screen, never within `postLossQuietMinutes` of a loss. The Shop the player opens is not an
 * offer and is always there.
 */
export function offersAllowed(balance: BalanceData, screen: OfferScreen, now: number, lastLossAt: number): boolean {
  if (screen !== "hub") return false;
  const quiet = balance.commerce.shop.postLossQuietMinutes * SECONDS_PER_MINUTE;
  // A clock set back past the loss ends the quiet (it cannot tell how long ago the loss was).
  return lastLossAt < 0 || now - lastLossAt >= quiet || now < lastLossAt;
}

/** Purchases are made in the hub only: never in a level or from the end screen (no purchase prompt in battle). */
export function purchasesAllowed(screen: OfferScreen): boolean { return screen === "hub"; }

/**
 * Where a gem pack may be bought: the hub, and ONE battle screen (Kevin, 2026-10-13: "add a way to open the shop for
 * top ups") — the Starfall Wyrm's choice (`wyrmChoice`), where the battle already stands paused on the Commander's
 * answer and CHALLENGE AGAIN costs gems. Gem packs only: Deals, the season pass, cosmetics and every gem or gold spend
 * keep `purchasesAllowed` (hub only), and nothing is ever offered unasked (`offersAllowed`).
 */
export type GemPackScreen = OfferScreen | "wyrmChoice";
export function gemPacksAllowed(screen: GemPackScreen): boolean { return screen === "hub" || screen === "wyrmChoice"; }

/** Seconds until a UTC day's crates and caps reset (for the "new crates in 5 h" line; no countdown push). */
export function secondsToNextDay(now: number): number { return (utcDay(now) + 1) * SECONDS_PER_DAY - now; }
